import { Fragment } from 'react';
import { Mail } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { assetUrl } from '../../../../common/helpers';
import GetParticipantSentMailsQuery from './GetParticipantSentMailsQuery';
import UI from '../../../../common/UI';
import config from '../../../../config';
import useLocale from '../../../../common/useLocale';
import useProject from '../../../useProject';
import useQuery from '../../../../api/useQuery';

interface CommunicationsPageProps {
  participantId: string;
}

const CommunicationPage = ({ participantId }: CommunicationsPageProps) => {
  const { t } = useTranslation();
  const { formatDate, parseDate } = useLocale();
  const project = useProject();

  const { data } = useQuery(
    GetParticipantSentMailsQuery,
    {
      variables: {
        project_id: project.id,
        participant_id: participantId,
      },
    },
  );

  if (!data) {
    return <UI.PageLoader />;
  }

  const sentMails = data.project.participant.sent_mails;

  return (
    <UI.Section>
      <UI.GridContainer>
        <UI.SectionHeading>
          {t('communication')}
        </UI.SectionHeading>

        <UI.Card sc={{ hasSidePanel: sentMails.length > 0 }}>
          {sentMails.length > 0 && (
            <UI.Div>
              <UI.Legend>
                {t('sent_mails')}
              </UI.Legend>
            </UI.Div>
          )}

          <UI.Div>
            <UI.GridContainer sc={{ gutter: 0.75 }}>
              {sentMails.map((sentMail, index) => (
                <Fragment key={sentMail.id}>
                  {index > 0 && <UI.HR />}
                  <UI.GridContainer
                    sc={{
                      columns: ['1fr', '4fr 1fr'],
                      gutter: 0.5,
                    }}
                  >
                    <UI.Div>
                      <UI.Truncate>
                        <UI.A
                          href={`${config.backendPath}/api/sent_mails/${sentMail.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sc={{ secondary: true }}
                          style={{ fontWeight: 500 }}
                        >
                          <UI.Icon sc={{ muted: true }}>
                            <Mail />
                          </UI.Icon>
                          {' '}
                          {sentMail.subject}
                        </UI.A>
                      </UI.Truncate>
                      {sentMail.sent_mail_attachments.length > 0 && (
                        <UI.Div sc={{ small: true, mt: 1 }}>
                          {sentMail.sent_mail_attachments.map((attachment) => (
                            <Fragment key={attachment.id}>
                              {' '}
                              <UI.AButton
                                href={`${config.backendPath}/api/sent_mail_attachments/${attachment.id}`}
                                target="_blank"
                                sc={{ size: 'sm', outline: true }}
                                rel="noopener noreferrer"
                              >
                                {attachment.filename}
                              </UI.AButton>
                            </Fragment>
                          ))}
                        </UI.Div>
                      )}
                    </UI.Div>
                    <UI.Small sc={{ muted: true, textAlign: ['left', 'right'] }}>
                      {formatDate(
                        parseDate(sentMail.created_at, { timezone: 'UTC' }),
                        { format: 'display_compact_date_time' },
                      )}
                    </UI.Small>
                  </UI.GridContainer>
                </Fragment>
              ))}

              {sentMails.length === 0 && (
                <UI.Div sc={{ padding: 4, textAlign: 'center' }}>
                  <UI.Div>
                    <img src={assetUrl('images/communicate.svg')} alt="" height={120} />
                  </UI.Div>
                  <UI.Span>{t('no_sent_mails_for_participant')}</UI.Span>
                </UI.Div>
              )}
            </UI.GridContainer>
          </UI.Div>
        </UI.Card>
      </UI.GridContainer>
    </UI.Section>
  );
};

export default CommunicationPage;
